import { useState } from 'react';

const POSITION_TIMEOUT_MS = 10000;
// getCurrentPosition's own timeout only starts once permission is granted; a
// permission prompt the user never answers would otherwise leave the caller
// waiting forever.
const WATCHDOG_MS = POSITION_TIMEOUT_MS + 5000;

// GeolocationPositionError codes.
const ERROR_CODES = { 1: 'denied', 2: 'unavailable', 3: 'timeout' };

// Captures the browser's geolocation as { lat, lng } for delivery-address
// coordinates. Used as a quick-fill shortcut alongside the map picker
// (LocationPickerMap), which lets the customer pin a different location, and
// by the browsing-location dialog. `locationErrorCode` says why the last
// attempt failed: unsupported | denied | unavailable | timeout.
//
// captureLocation() resolves { lat, lng } or null. captureLocation({
// withReason: true }) resolves { coordinates, errorCode } instead, for callers
// that need the reason straight away rather than on the next render.
const useCurrentLocation = () => {
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [locationErrorCode, setLocationErrorCode] = useState(null);

  const captureLocation = ({ withReason = false } = {}) =>
    new Promise((settle) => {
      const resolve = (coordinates, errorCode = null) => settle(withReason ? { coordinates, errorCode } : coordinates);
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        setLocationError('Location services are not available in this browser.');
        setLocationErrorCode('unsupported');
        resolve(null, 'unsupported');
        return;
      }
      setLocating(true);
      setLocationError(null);
      setLocationErrorCode(null);

      let settled = false;
      const fail = (code) => {
        if (settled) return;
        settled = true;
        clearTimeout(watchdog);
        setLocating(false);
        setLocationError('Could not get your location. Please allow location access and try again.');
        setLocationErrorCode(code);
        resolve(null, code);
      };
      const watchdog = setTimeout(() => fail('timeout'), WATCHDOG_MS);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (settled) return;
          settled = true;
          clearTimeout(watchdog);
          setLocating(false);
          resolve({ lat: position.coords.latitude, lng: position.coords.longitude });
        },
        (error) => fail(ERROR_CODES[error?.code] || 'unavailable'),
        { enableHighAccuracy: true, timeout: POSITION_TIMEOUT_MS }
      );
    });

  return { locating, locationError, locationErrorCode, captureLocation };
};

export default useCurrentLocation;
